import './login-form.component.scss';

import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { useTranslation } from '../../../i18n';
import type { AuthenticationResult } from '../../models/auth.model';
import { AuthenticationFormErrorCode } from '../../models/auth.model';
import { authenticationFormService } from '../../services/authentication-form.service';

type Props = {
  returnToUrl: string;
  terms?: string;
  showCreateAcct?: boolean;
  onClose?: (result: AuthenticationResult) => void;
  onShowAcctCreate?: () => void;
  onShowReset?: (e?: Event) => void;
};

export function LoginForm({
  returnToUrl,
  terms,
  showCreateAcct = true,
  onShowAcctCreate,
  onShowReset,
  onClose,
}: Props) {
  const actionUrl = '/account/login';
  const formRef = useRef<HTMLFormElement>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [acceptsTerms, setAcceptsTerms] = useState(false);
  const { t } = useTranslation();

  const displayTerms = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowTerms(true);
  };

  const handleLogin = async (event: Event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = formRef.current!;
    const email = (form.querySelector('#email') as HTMLInputElement)?.value || '';
    const password = (form.querySelector('#password') as HTMLInputElement)?.value || '';

    const body = new URLSearchParams();
    body.set('form_type', 'customer_login');
    body.set('utf8', '✓');
    body.set('return_to', returnToUrl);
    body.set('customer[email]', email);
    body.set('customer[password]', password);

    setLoading(true);
    setErrorMessage(null);

    await authenticationFormService.submitForm(actionUrl, returnToUrl, form, body, (response) => {
      if (response.type === 'ERROR_CODE') {
        setErrorMessage(response.errorMessage || t('auth.errorUnableToSignIn'));
        if (
          response.errorCode === AuthenticationFormErrorCode.UNKNOWN_ERROR &&
          (response as any).error
        ) {
          setErrorMessage(t('auth.errorLoginGeneric'));
          console.error('Login error:', (response as any).error ?? (response as any).response);
        }
      } else if (response.type === 'SUCCESS') {
        onClose?.({
          success: true,
          termsAccepted: acceptsTerms,
          optInToMarketingEmails: true,
        });
      }
      setLoading(false);
    });

    return false;
  };

  return (
    <form
      id="customer_login"
      accept-charset="UTF-8"
      class="single-login-form"
      onSubmit={handleLogin}
      ref={formRef}
    >
      {errorMessage && (
        <div class="single-error-container" dangerouslySetInnerHTML={{ __html: errorMessage }} />
      )}

      {!showTerms && (
        <section class={`${showTerms ? 'single-d-none' : ''}`}>
          <div class="single-input-group">
            <label for="email">{t('auth.email')}</label>
            <input
              id="email"
              type="email"
              name="customer[email]"
              autocomplete="email"
              placeholder={t('auth.email')}
            />
          </div>

          <div class="single-input-group">
            <label for="password">{t('auth.password')}</label>
            <input
              id="password"
              type="password"
              name="customer[password]"
              placeholder={t('auth.password')}
            />
          </div>

          {terms && (
            <div class="single-input-group single-checkbox">
              <input
                id="accepts-terms"
                type="checkbox"
                name="customer[accepts_terms]"
                onChange={(e) => setAcceptsTerms((e.target as HTMLInputElement).checked)}
              />
              <label for="accepts-terms">
                {t('auth.acceptTerms')}{' '}
                <span class="terms-link cursor-pointer" onClick={displayTerms}>
                  {t('auth.termsAndConditions')}
                </span>
              </label>
            </div>
          )}

          <div class="single-recover-links">
            <span onClick={(e) => onShowReset?.(e as unknown as Event)}>
              {t('auth.resetPassword')}
            </span>
            {showCreateAcct && (
              <>
                <span class="divider"> • </span>
                <span onClick={() => onShowAcctCreate?.()}>{t('auth.createAccount')}</span>
              </>
            )}
          </div>

          <button
            class="btn button single-submit-btn"
            type="submit"
            disabled={loading || (!!terms && !acceptsTerms)}
          >
            {loading ? t('auth.signingIn') : t('auth.signIn')}
          </button>
        </section>
      )}

      {showTerms && (
        <div>
          <span class="go-back cursor-pointer" onClick={() => setShowTerms(false)}>
            {t('auth.back')}
          </span>
          <div class="legal-terms" dangerouslySetInnerHTML={{ __html: terms || '' }} />
        </div>
      )}
    </form>
  );
}
